import { Badge, notification, Spin } from "antd";
import { CheckOutlined, SendOutlined } from "@ant-design/icons";
import { InternalPageLayout } from "components";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

interface WhatsAppConversation {
    conversationId: string;
    contactWaId: string;
    displayName: string | null;
    phoneNumber: string;
    lastMessageAt: string;
    sessionExpiresAt: string;
    sessionIsActive: boolean;
    totalMessages: number;
}

interface ConversationDetail extends WhatsAppConversation {
    returnedMessages: number;
    skip: number;
    take: number;
    userMessages: any[];
    botMessages: any[];
}

const getAvatarInitials = (displayName?: string | null) => {
    const value = displayName?.trim();
    if (!value) return "WA";
    const parts = value.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatDateTime = (value: string) => dayjs(value).format("DD/MMM/YYYY hh:mm A");

const formatPhoneNumber = (value?: string | null) => {
    if (!value) return "Sin número";

    const digits = value.replace(/\D/g, "");
    const localNumber = digits.length === 13 && digits.startsWith("521")
        ? digits.slice(3)
        : digits;

    if (localNumber.length !== 10) return value;

    return `${localNumber.slice(0, 2)} ${localNumber.slice(2, 6)} ${localNumber.slice(6)}`;
};

const WhatsAppBot = () => {
    const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
    const [selectedChatTitle, setSelectedChatTitle] = useState<string>("");
    const [selectedChatTitlePhone, setSelectedChatTitlePhone] = useState<string>("");
    const [chatLoading, setChatLoading] = useState(false);
    const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(null);
    const [panelHeight, setPanelHeight] = useState<number>(window.innerWidth > 1800 ? 750 : 520);

    useEffect(() => {
        const updatePanelHeight = () => {
            setPanelHeight(window.innerWidth > 1800 ? 750 : 520);
        };

        updatePanelHeight();
        window.addEventListener('resize', updatePanelHeight);
        return () => window.removeEventListener('resize', updatePanelHeight);
    }, []);

    useEffect(() => {
        const loadConversations = async () => {
            setIsLoading(true);
            try {
                const response = await fetch("https://message.goodlook.com.mx/api/conversations");
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const conversationsData = await response.json();
                if (!Array.isArray(conversationsData)) {
                    throw new Error("La respuesta no es un arreglo de conversaciones");
                }
                const sorted = conversationsData.sort((a, b) =>
                    dayjs(b.lastMessageAt).valueOf() - dayjs(a.lastMessageAt).valueOf()
                );
                setConversations(sorted);
            } catch (error) {
                console.error("Error cargando conversaciones:", error);
                notification.error({
                    message: "Error",
                    description: "No se pudo cargar el historial de conversaciones"
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadConversations();
    }, []);

    const conversationItems = useMemo(() => {
        return conversations.map((conversation) => {
            const isActive = conversation.sessionIsActive;
            const formattedPhone = formatPhoneNumber(conversation.phoneNumber);
            const title = conversation.displayName?.trim() || formattedPhone || "Sin nombre";
            return (
                <div
                    key={conversation.conversationId}
                    onClick={() => {
                        setSelectedPhone(conversation.phoneNumber);
                        setSelectedChatTitle(title);
                        setSelectedChatTitlePhone(formattedPhone);
                    }}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: 16,
                        borderBottom: "1px solid #D8CDBF",
                        background: selectedPhone === conversation.phoneNumber ? "#E9E2D8" : (isActive ? "#EEE4D3" : "#F1E6D8"),
                        color: isActive ? "#4A4238" : "#7E6954",
                        opacity: isActive ? 1 : 0.85,
                        cursor: "pointer"
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                        <div
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                background: isActive ? "#7E6954" : "#DCDCCF",
                                color: isActive ? "#FFF8E6" : "#6B5A49",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: 14,
                                flexShrink: 0
                            }}
                        >
                            {getAvatarInitials(title)}
                        </div>
                        <div style={{ minWidth: "88%" }}>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {title}
                            </div>
             
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, width: "100%", minWidth: 0 }}>
                                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    Total Mensajes: {conversation.totalMessages}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div style={{ textAlign: "right", minWidth: 100, fontSize: 12, whiteSpace: "nowrap", height: 50, lineHeight: "20px", flexDirection: "column", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 4, textTransform: "capitalize" }}>

                                                        <Badge
                                    style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                                    status={isActive ? "success" : "error"}
                                    text={isActive ? "Activo" : "Inactivo"}
                                />
                                                        {formatDateTime(conversation.lastMessageAt)}
                    </div>
                </div>
            );
        });
    }, [conversations, selectedPhone]);

    useEffect(() => {
        if (!selectedPhone) return;
        const loadByPhone = async () => {
            setChatLoading(true);
            try {
                const resp = await fetch(`https://message.goodlook.com.mx/api/conversations/by-phone/${selectedPhone}`);
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const data = await resp.json();
                setSelectedConversation(data);
            } catch (err) {
                console.error(err);
                notification.error({ message: 'Error', description: 'No se pudo cargar la conversación seleccionada' });
            } finally {
                setChatLoading(false);
            }
        };
        loadByPhone();
    }, [selectedPhone]);

    const interleaveMessages = (user: any[], bot: any[]) => {
        const out: any[] = [];
        const max = Math.max(user?.length || 0, bot?.length || 0);
        for (let i = 0; i < max; i++) {
            if (user && user[i]) out.push(user[i]);
            if (bot && bot[i]) out.push(bot[i]);
        }
        return out;
    };

    const ChatView = ({ conversation, chatTitle, selectedChatTitlePhone }: { conversation: ConversationDetail; chatTitle: string; selectedChatTitlePhone: string }) => (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "start", gap: 12, paddingBottom: 12, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: conversation.sessionIsActive ? "#7E6954" : "#DCDCCF", color: conversation.sessionIsActive ? "#FFF8E6" : "#6B5A49", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                    {getAvatarInitials(chatTitle)}
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{chatTitle}</div>
                    <div style={{ fontWeight: 400 }}>{selectedChatTitlePhone}</div>
                    <div style={{ fontSize: 12, color: "#5C5244" }}>{conversation.sessionIsActive ? "Activo" : "Inactivo"}</div>
                </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
                {chatLoading ? (
                    <div style={{ textAlign: "center" }}><Spin spinning tip="Cargando chat..." /></div>
                ) : (
                    interleaveMessages(conversation.userMessages || [], conversation.botMessages || []).map((m: any, idx: number) => {
                        const isBot = m.sender === 'business' || m.fromMe === true;
                        return (
                            <div key={m.messageId || idx} style={{ display: "flex", justifyContent: isBot ? 'flex-end' : 'flex-start' }}>
                                <div style={{
                                    maxWidth: '70%',
                                    padding: '10px 12px',
                                    borderRadius: 12,
                                    background: isBot ? '#DCF8C6' : '#FFFFFF',
                                    color: '#111',
                                    boxShadow: '0 1px 0 rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                                    <div style={{ display: 'flex', justifyContent: isBot ? 'flex-end' : 'flex-start', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11, color: '#666' }}>
                                        <span>{dayjs(m.eventTimestamp).format('hh:mm A')}</span>
                                        {isBot && m.status && <CheckOutlined style={{ color: m.status === 'read' ? '#1890ff' : '#999', fontSize: 12 }} />}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            <div style={{ paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <textarea
                        disabled
                        placeholder="Escribe un mensaje..."
                        style={{
                            flex: 1,
                            minHeight: 56,
                            resize: 'none',
                            borderRadius: 20,
                            border: '1px solid rgba(0,0,0,0.12)',
                            padding: '12px 16px',
                            background: '#F2F2F2',
                            color: '#7E7E7E',
                            outline: 'none'
                        }}
                    />
                    <button
                        disabled
                        style={{
                            width: 48,
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            border: 'none',
                            background: '#D8D8D8',
                            color: '#7E7E7E',
                            cursor: 'not-allowed'
                        }}
                        title="Enviar mensaje"
                    >
                        <SendOutlined />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <InternalPageLayout title="Bitácora Bot">
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div style={{
                    height: panelHeight,
                    minWidth: 320,
                    background: "#FFFFFF",
                    borderRadius: 20,
                    boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
                    overflow: "hidden"
                }}>
                    <div style={{ padding: 22, background: "#FF0072", color: "#FFFFFF" }}>
                        <h3 style={{ margin: 0, fontSize: 18, color: " #FFFFFF" }}>Conversaciones</h3>
                        <p style={{ margin: "8px 0 0", color: "#FFFFFF", fontSize: 13 }}>Historial ordenado por última actividad</p>
                    </div>
                    <div style={{ height: panelHeight, overflowY: 'auto', background: "#F7F5EF" }}>
                        <Spin spinning={isLoading} tip="Cargando conversaciones...">
                            {conversationItems.length > 0 ? (
                                conversationItems
                            ) : (
                                <div style={{ padding: 40, textAlign: "center", color: "#7E6954" }}>
                                    {isLoading ? null : <span>No hay conversaciones disponibles.</span>}
                                </div>
                            )}
                        </Spin>
                    </div>
                </div>

                <div style={{
                    flex: 1,
                    minWidth: 320,
                    background: "#F1ECE1",
                    borderRadius: 20,
                    display: "flex",
                    flexDirection: "column",
                    padding: 24,
                    height: panelHeight,
                    color: "#6B5A49",
                    boxShadow: "0 8px 28px rgba(0,0,0,0.05)"
                }}>
                    {selectedConversation ? (
                        <ChatView conversation={selectedConversation} chatTitle={selectedChatTitle} selectedChatTitlePhone={selectedChatTitlePhone} />
                    ) : (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                            <div style={{textAlign: 'center'}}>
                                <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Selecciona una conversación</p>
                                <p style={{ marginTop: 12, color: "#5C5244" }}>Aquí aparecerá el contenido cuando elijas un chat.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </InternalPageLayout>
    );
};

export default WhatsAppBot;
